import {
  StackLayout,
  Card,
  ParagraphText,
  Divider,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { useDocumentTitle } from '@leagueplatform/web-common';
import { useIntl } from '@leagueplatform/locales';
import {
  SECONDARY_CONTENT_MAX_WIDTH,
  DAY_MONTH_YEAR_DATE_FORMAT,
} from 'common/constants';
import { usePersonalInformation } from 'hooks/use-personal-information/use-personal-information.hook';
import { PageHeader } from 'components/page-header/page-header.component';
import { PageContentWrapper } from 'components/page-content-wrapper.component';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import React from 'react';
import { LabelValueItem } from 'pages/personal-information/label-value-item.component';
import { formatAddress, formatPhoneNumber } from 'utils/formatting';

export const EmailLinkFormatter = (children: React.ReactNode) => (
  <a href={`mailto:${children}`}>{children}</a>
);

export const PersonalInformation = () => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery(queryHelpers.only('mobile'));

  const {
    data: personalInformation,
    isLoading: isPersonalInfoLoading,
    error: personalInfoError,
  } = usePersonalInformation();

  useDocumentTitle(formatMessage({ id: 'STR_PERSONAL_INFORMATION' }));

  const homeLocation = personalInformation.userProfile?.homeLocation;
  const homeAddress =
    homeLocation?.address1 &&
    homeLocation?.city &&
    homeLocation?.postal &&
    homeLocation?.state
      ? formatAddress(
          homeLocation.address1,
          homeLocation.city,
          homeLocation.postal,
          homeLocation.state,
          homeLocation.address2 ?? undefined,
        )
      : '';

  const content = (
    <StackLayout orientation="vertical" spacing="$one">
      <ParagraphText size="sm">
        {formatMessage(
          { id: 'STR_PERSONAL_INFORMATION_DESCRIPTION' },
          {
            a: EmailLinkFormatter,
            email: 'OnlineDoctor@AmplarHealth.com.au',
          },
        )}
      </ParagraphText>
      <StackLayout
        orientation="vertical"
        spacing="$one"
        css={{ width: '100%' }}
        divider={<Divider />}
      >
        <LabelValueItem label={formatMessage({ id: 'STR_LEGAL_NAME' })}>
          {personalInformation.userProfile.displayName}
        </LabelValueItem>
        <LabelValueItem label={formatMessage({ id: 'STR_DATE_OF_BIRTH' })}>
          {personalInformation.userProfile.dateOfBirth &&
            new Date(
              personalInformation.userProfile.dateOfBirth,
            ).toLocaleDateString('en-AU', DAY_MONTH_YEAR_DATE_FORMAT)}
        </LabelValueItem>
        <LabelValueItem label={formatMessage({ id: 'STR_HOME_ADDRESS' })}>
          {homeAddress}
        </LabelValueItem>
        <LabelValueItem label={formatMessage({ id: 'STR_EMAIL_ADDRESS' })}>
          {personalInformation.email}
        </LabelValueItem>
        <LabelValueItem label={formatMessage({ id: 'STR_MOBILE_NUMBER' })}>
          {personalInformation.userProfile.phoneNumber &&
            formatPhoneNumber(personalInformation.userProfile.phoneNumber)}
        </LabelValueItem>
      </StackLayout>
    </StackLayout>
  );

  return (
    <PageContentWrapper css={{ maxWidth: SECONDARY_CONTENT_MAX_WIDTH }}>
      <PageHeader headingId="STR_PERSONAL_INFORMATION" showBackButton={false} />

      {personalInfoError && <>Error state</>}
      {isPersonalInfoLoading && <LoadingIndicator />}
      {!personalInfoError &&
        !isPersonalInfoLoading &&
        (isMobile ? (
          <div>{content}</div>
        ) : (
          <Card.Elevated backgroundColor="secondary">
            <Card.Section>{content}</Card.Section>
          </Card.Elevated>
        ))}
    </PageContentWrapper>
  );
};
